/* eslint-disable react/jsx-props-no-spreading */
import React, { createContext, useContext, useEffect } from 'react';

import { OnboardingSession } from 'middleware/Client';
import { apiInstance } from 'services';

const APIService = apiInstance();
const OnboardingContext = createContext();

const onboardingSessionKey = 'cureoscity_onboarding';
const onboardingSessionCompleteKey = 'cureoscity_onboarding_complete';
const onboardingDataKey = 'cureoscity_onboarding_data';

let currentOnboardingSession = null;
let currentOnboardingData = {};

const {
  namespaces: { general, onboarding, buildings }
} = APIService;

const OnboardingProvider = (props) => {
  const getOnboardingSession = () => currentOnboardingSession;

  useEffect(() => {
    try {
      const storedOnboardingSession = JSON.parse(
        localStorage.getItem(onboardingSessionKey)
      );
      const { id } = storedOnboardingSession;
      if (typeof id === 'string' && id.length > 0) {
        currentOnboardingSession = new OnboardingSession(
          storedOnboardingSession
        );
        try {
          const storedOnboardingData = JSON.parse(
            localStorage.getItem(onboardingDataKey)
          );
          if (
            storedOnboardingData !== null &&
            Object.prototype.toString.call(storedOnboardingData) ===
              '[object Object]'
          ) {
            currentOnboardingData = storedOnboardingData;
          }
        } catch (e) {
          currentOnboardingData = {};
        }
      } else {
        throw new Error('invalid_onboarding_session');
      }
    } catch (e) {
      localStorage.removeItem(onboardingSessionKey);
      localStorage.setItem(onboardingSessionCompleteKey, 'false');
      localStorage.removeItem(onboardingDataKey);
    }
  }, []);

  const startOnboarding = (companyId, buildingId) => {
    const { start } = onboarding;

    return new Promise((resolve, reject) => {
      start(companyId, buildingId)
        .then((onboardingSession) => {
          currentOnboardingSession = onboardingSession;
          localStorage.setItem(
            onboardingSessionKey,
            JSON.stringify(currentOnboardingSession)
          );
          resolve(currentOnboardingSession);
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const getOccupiers = () => onboarding.getOccupiers();

  const saveOnboardingStep = (step, data) =>
    // eslint-disable-next-line consistent-return
    new Promise((resolve, reject) => {
      const currentStep = parseInt(step, 10);

      let dataKey = '';
      let dataValue = data;
      const saveData = ![
        'string',
        'number',
        'bigint',
        'symbol',
        'function'
      ].includes(typeof data)
        ? { ...data }
        : data;
      const saveOnboardingData = () => {
        localStorage.setItem(
          onboardingDataKey,
          JSON.stringify(currentOnboardingData)
        );
      };

      if (currentStep === 1) {
        const { companyId } = data.selectedCompany;
        const { buildingId } = data.selectedBuilding;
        startOnboarding(companyId, buildingId)
          .then((response) => {
            currentOnboardingData = { step1: saveData };
            saveOnboardingData();
            resolve(response);
          })
          .catch((error) => reject(error));
      } else {
        if (currentStep === 2) {
          const dateSplit = dataValue.split('/');
          dataValue = `${dateSplit[2]}-${dateSplit[1]}-${dateSplit[0]}`;
          dataKey = 'date_of_birth';
        } else if (currentStep === 3) {
          saveData.password = null;
          dataKey = Object.keys(dataValue);
          dataValue = Object.values(dataValue);
        } else if (currentStep === 4) {
          dataKey = 'role_id';
        } else if (currentStep === 5) {
          dataKey = 'travel_options';
        } else if (currentStep === 6) {
          dataKey = 'floor_id';
        } else if (currentStep === 7) {
          dataKey = 'profile_picture_uri';
        } else if (currentStep === 8) {
          dataKey = 'terms_and_conditions';
        }

        currentOnboardingData[`step${currentStep}`] = saveData;
        saveOnboardingData();

        if (dataKey.length || Array.isArray(dataKey))
          resolve(onboarding.save(getOnboardingSession(), dataKey, dataValue));
        reject(new Error('invalid_key'));
      }
    });
  return (
    <OnboardingContext.Provider
      value={{
        upload: ({ file, data, onProgress }) => {
          const { id } = getOnboardingSession();
          return general.upload({
            file,
            data: {
              ...data,
              onboarding_session_id: id
            },
            onProgress
          });
        },
        getOnboardingSession,
        getOnboardingStepData: (step) => {
          const stepKey = `step${step}`;
          if (
            Object.prototype.hasOwnProperty.call(currentOnboardingData, stepKey)
          ) {
            return currentOnboardingData[stepKey];
          }
          return null;
        },
        startOnboarding: (selectedBuilding) =>
          startOnboarding(selectedBuilding.id, selectedBuilding.building.id),
        getOccupiers,
        complete: () =>
          onboarding.complete(getOnboardingSession()).then((data) => {
            localStorage.setItem(onboardingSessionCompleteKey, 'true');
            return data;
          }),
        saveOnboardingStep,
        generatePin: () => onboarding.generatePin(getOnboardingSession()),
        verifyPin: (pin) => onboarding.validatePin(getOnboardingSession(), pin),
        getJobRoles: () => onboarding.getJobRoles(getOnboardingSession()),
        getTravelOptions: () =>
          onboarding.getTravelOptions(getOnboardingSession()),
        getFloors: () => onboarding.getFloors(getOnboardingSession()),
        getRegistrationTerms: (buildingId) =>
          buildings.getRegistrationTerms(buildingId)
      }}
      {...props}
    />
  );
};

const useOnboarding = () => useContext(OnboardingContext);

export { OnboardingProvider, useOnboarding };
